<script lang="ts" setup>
	import type { ClassContext } from '~/types'

	const {
		navigator = true,
		indicators = false,
		ui = { default: 'sm:pt-1 w-full' }
	} = defineProps<{
		indicators?: boolean
		navigator?: boolean
		ui?: {
			default?: ClassContext
			btn?: {
				active?: any
				height?: string
				width?: string
				translateY?: string
			}
			wrapper?: Partial<{
				default: string
				overflow: string
				height: string
				position: string
				gap: string
				display: string
				align: string
				padding: string
			}>
			indicator?: {
				default?: string
				active?: string
				inactive?: string
				wrapper?: string
			}
		}
	}>()

	const configCls: typeof ui = {
		default: '',
		indicator: {
			default: '',
			active: 'bg-main',
			inactive: 'bg-gray-300',
			wrapper: 'mt-3'
		},
		wrapper: {
			default: '',
			display: 'flex',
			gap: 'gap-x-5 md:gap-x-[10px]',
			position: 'relative',
			overflow: 'invisible-scrollbar overflow-x-auto overflow-hidden snap-x',
			height: 'h-fit',
			align: 'items-stretch',
			padding: 'md:px-3'
		}
	}

	const el = useTemplateRef('carousel-wrapper')
	const carouselRef = useTemplateRef('carouselWrapper')
	const totalNodeCount = shallowRef(0)
	const activeNode = shallowRef(0)
	const showArrows = ref(false)

	const { showLeftArrow, showRightArrow, scrollLeftAction: prev, scrollRightAction: next } = useCarousel(el)

	defineExpose({ carouselRef })
</script>

<template>
	<div
		ref="carouselWrapper"
		:class="[ui?.default || '', 'relative overflow-hidden']"
		role="region"
		aria-roledescription="carousel"
	>
		<div
			ref="carousel-wrapper"
			:class="[
				ui?.wrapper?.default || '',
				ui?.wrapper?.padding || configCls.wrapper?.padding,
				ui?.wrapper?.align || configCls.wrapper?.align,
				ui?.wrapper?.display || configCls.wrapper?.display,
				ui?.wrapper?.gap || configCls.wrapper?.gap,
				ui?.wrapper?.position || configCls.wrapper?.position,
				ui?.wrapper?.overflow || configCls.wrapper?.overflow,
				ui?.wrapper?.height || configCls.wrapper?.height
			]"
		>
			<slot />
		</div>

		<div
			class="flex items-center gap-x-2 justify-center"
			:class="[ui?.indicator?.wrapper || configCls?.indicator?.wrapper]"
			v-if="indicators"
		>
			<button
				v-for="(_, index) in totalNodeCount"
				:key="'indicator-' + index"
				type="button"
				aria-label="carousel indicators"
				class="size-3 rounded-full"
				:class="[
					ui?.indicator?.default || '',
					index === activeNode
						? ui?.indicator?.active || configCls.indicator?.active
						: ui?.indicator?.inactive || configCls.indicator?.inactive
				]"
			/>
		</div>

		<div v-if="navigator" class="flex items-center my-auto w-full z-20 left-0">
			<slot
				name="button-group"
				aria-label="left arrow"
				:show-arr="showArrows"
				:total-node-count="totalNodeCount"
				:current-node="activeNode"
				:prev
				:next
			>
				<button
					v-if="showLeftArrow"
					class="absolute left-0 z-10 top-1/2 transform -translate-y-1/2 p-2"
					aria-label="push left"
					@click="prev"
				>
					<img
						src="/assets/img/arrow.gif"
						alt="indicator"
						class="max-w-[60px] rotate-180 z-10"
						loading="lazy"
					/>
				</button>
				<button
					v-if="showRightArrow"
					aria-label="push right"
					class="absolute right-0 top-1/2 transform disabled:cursor-not-allowed -translate-y-1/2 p-2"
					@click="next"
				>
					<img src="/assets/img/arrow.gif" alt="indicator" class="max-w-[60px]" loading="lazy" />
				</button>
			</slot>
		</div>
	</div>
</template>

<style scoped>
	.btn {
		@apply h-[6px] rounded-full max-w-[18px];
	}
</style>
